import React, { useState } from "react";
import AsyncSelect from "react-select/async";
import { putData } from "utils/Gateway";
import * as url from "helper/UrlHelper";
import { useTranslation } from "react-i18next";
import { getCustomStyles } from "Config/Config";
import { getMemberOptions } from "helper/ContactHelper/ContactHelper";

const AssignUserModal = ({
  moduleName = "",
  afterModalClose = () => {},
  selectedIds = [],
  setShowAlert = () => {},
  setAlertMessage = () => {},
  setMessageType = () => {},
  onModalClose = () => {},
}) => {
  const { t } = useTranslation();
  const token = localStorage.getItem("token");

  const [selectedUserValue, setSelectedUserValue] = useState(null);
  const [selectedUserId, setselectedUserId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Validation
  const validationHandler = () => {
    const newErrors = {};
    if (!selectedUserValue || !selectedUserValue.value) {
      newErrors.userWarning = t("Please select users");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Async load options
  const loadUserOptions = async (inputValue) => {
    try {
      // Preload options if input is empty, or load matching if input length > 1
      if (!inputValue || inputValue.length === 0 || inputValue.length > 1) {
        const response = await getMemberOptions(inputValue);
        return response;
      }
      return [];
    } catch {
      return [];
    }
  };

  // Select users
  const usersSelectionHandler = (val) => {
    if (val) {
      setSelectedUserValue(val);
      setselectedUserId(val.value);
    } else {
      setSelectedUserValue(null);
      setselectedUserId(null);
    }
  };

  // No save logic, just close modal and callback
  const assignHandler = async () => {
    if (!validationHandler()) {
      setShowAlert(true);
      setAlertMessage("Please select users for assignment");
      setMessageType("error");
      return;
    }

    try {
      setIsSaving(true);

      let requestUrl = url.API_BASE_URL;

      let apiData = {
        selectedids: selectedIds,
        assignto: selectedUserId,
      };

      switch (moduleName) {
        case "prescription":
          requestUrl +=
            url.API_ASSIGN_USER_MEDICAL_DOCUMENT + `?token=${token}`;
          break;
        case "condition":
          requestUrl +=
            url.API_ASSIGN_USER_MEDICAL_CONDITION + `?token=${token}`;
          break;
        case "allergy":
          requestUrl += url.API_ASSIGN_USER_ALLERGY + `?token=${token}`;
          break;
        case "immunization":
          requestUrl += url.API_ASSIGN_USER_IMMUNIZATION + `?token=${token}`;
          break;
        case "medicaltest":
          requestUrl += url.API_ASSIGN_USER_MEDICAL_TEST + `?token=${token}`;
          break;
        case "bloodpressure":
          requestUrl += url.API_ASSIGN_USER_BLOOD_PRESSURE + `?token=${token}`;
          break;
        default:
          setShowAlert(true);
          setAlertMessage("Module not found");
          setMessageType("error");
          break;
      }

      const response = await putData(requestUrl, apiData);

      if (response.status) {
        resetHandler();
        afterModalClose(response.data);
        // close modal
        const bootstrapModal = document.querySelector("#assignUserModal");
        if (window.bootstrap && bootstrapModal) {
          const myModal = window.bootstrap.Modal.getInstance(bootstrapModal);
          myModal && myModal.hide();
        }
      }
    } catch (error) {
      setShowAlert(true);
      setAlertMessage(error.message);
      setMessageType("error");
    } finally {
      setIsSaving(false);
    }
  };

  // Reset modal
  const resetHandler = () => {
    setSelectedUserValue(null);
    setselectedUserId(null);
    setErrors({});
    onModalClose();
  };

  return (
    <div
      className="modal fade"
      id="assignUserModal"
      tabIndex="-1"
      aria-labelledby="assignUserModal"
      aria-hidden="true"
      data-bs-backdrop="static"
    >
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content bg-white border-0 rounded-15">
          <div className="modal-header bg-primary text-white px-4 py-3 border-bottom border-gray-300">
            <div className="d-flex align-items-center gap-3">
              <div className="icon-wrapper p-3 bg-white rounded-circle d-flex align-items-center justify-content-center">
                <span className="material-symbols-outlined text-primary icon-md">
                  person_add
                </span>
              </div>
              <div>
                <h3
                  className="fw-bold fs-4 text-white mb-0"
                  id="assignUserModalLabel"
                >
                  {t("Assign Users")}
                </h3>
                <p className="text-white fs-sm mb-0">
                  {t("Choose users to assign")}
                </p>
              </div>
            </div>
            <button
              type="button"
              className="btn-close p-1 bg-gray-200 rounded-circle shadow-sm border-0"
              data-bs-dismiss="modal"
              aria-label="Close"
              onClick={() => {
                resetHandler();
                onModalClose();
              }}
            ></button>
          </div>
          <div className="modal-body p-4">
            <div className="p-3 p-md-4 border border-gray-300 rounded-10 shadow-sm bg-light">
              {isSaving && (
                <div
                  className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center bg-white bg-opacity-75"
                  style={{ zIndex: 1050 }}
                >
                  <div
                    className="spinner-border text-primary mb-3"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="text-primary fw-semibold mb-0">
                    {t("Assigning users")}, {t("please wait")}...
                  </p>
                </div>
              )}
              <form
                onSubmit={(e) => e.preventDefault()}
                className="d-flex flex-column h-100"
              >
                <div className="fields-container flex-fill">
                  <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                    <label
                      htmlFor="selectList"
                      className="d-block fs-sm fw-semibold mb-2 text-dark"
                    >
                      {t("Select Users to Assign")}
                    </label>
                    <AsyncSelect
                      isClearable
                      cacheOptions={false}
                      defaultOptions={[]}
                      loadOptions={loadUserOptions}
                      placeholder={t("Search or select users by name or email")}
                      value={selectedUserValue}
                      onChange={usersSelectionHandler}
                      styles={getCustomStyles(errors.userWarning)}
                    />
                    {errors.userWarning && (
                      <div className="invalid-feedback d-block">
                        * {errors.userWarning}
                      </div>
                    )}
                  </div>
                </div>
                <div className="action d-flex justify-content-end pt-3 border-top mt-auto">
                  <button
                    type="button"
                    className="btn btn-primary d-flex align-items-center gap-2"
                    onClick={assignHandler}
                    style={{ cursor: isSaving ? "not-allowed" : "pointer" }}
                    disabled={isSaving}
                  >
                    <span className="material-symbols-outlined me-1">add</span>
                    {t("Assign Users")}
                    {isSaving && (
                      <div
                        className="mx-2 spinner-border spinner-border-sm"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignUserModal;
